type UrlFlagStorage = "local" | "session";

type UrlFlagDefinition = {
    storage: UrlFlagStorage;
    queryParam?: string;
};

export const urlFlagDefinitions: Record<string, UrlFlagDefinition> = {
    tour: {
        storage: "session",
    },
    userRegistry: {
        storage: "local",
    },
    homePagePopup: {
        storage: "session",
    },
};

export type UrlFlagName = keyof typeof urlFlagDefinitions;

const URL_FLAG_STORAGE_PREFIX = "urlFlag:";

function getStorage(storageType: UrlFlagStorage): Storage | null {
    if (typeof window === "undefined") {
        return null;
    }

    return storageType === "local" ? window.localStorage : window.sessionStorage;
}

function getStorageKey(flagName: UrlFlagName): string {
    return `${URL_FLAG_STORAGE_PREFIX}${flagName}`;
}

function getDefinition(flagName: UrlFlagName): UrlFlagDefinition {
    return urlFlagDefinitions[flagName];
}

function isTruthyFlagValue(value: string | null): boolean {
    if (value === null) {
        return false;
    }

    if (value === "") {
        return true;
    }

    return ["true", "1", "yes", "on"].includes(value.toLowerCase());
}

export function persistUrlFlagsFromSearch(search = typeof window !== "undefined" ? window.location.search : ""): void {
    if (typeof window === "undefined") {
        return;
    }

    const searchParams = new URLSearchParams(search);

    (Object.entries(urlFlagDefinitions) as [UrlFlagName, (typeof urlFlagDefinitions)[UrlFlagName]][]).forEach(
        ([flagName, definition]) => {
            const queryParam = definition.queryParam ?? flagName;

            if (!searchParams.has(queryParam)) {
                return;
            }

            const storage = getStorage(definition.storage);
            if (!storage) {
                return;
            }

            storage.setItem(getStorageKey(flagName), searchParams.get(queryParam) ?? "");
        }
    );
}

export function getUrlFlag(flagName: UrlFlagName): string | null {
    const storage = getStorage(getDefinition(flagName).storage);
    return storage?.getItem(getStorageKey(flagName)) ?? null;
}

export function getBooleanUrlFlag(flagName: UrlFlagName): boolean {
    return isTruthyFlagValue(getUrlFlag(flagName));
}

export function setUrlFlag(flagName: UrlFlagName, value: string): void {
    const storage = getStorage(getDefinition(flagName).storage);
    storage?.setItem(getStorageKey(flagName), value);
}

export function clearUrlFlag(flagName: UrlFlagName): void {
    const storage = getStorage(getDefinition(flagName).storage);
    storage?.removeItem(getStorageKey(flagName));
}

export function getAllUrlFlags(): Partial<Record<UrlFlagName, string>> {
    return (Object.keys(urlFlagDefinitions) as UrlFlagName[]).reduce<Partial<Record<UrlFlagName, string>>>(
        (flags, flagName) => {
            const value = getUrlFlag(flagName);

            if (value !== null) {
                flags[flagName] = value;
            }

            return flags;
        },
        {}
    );
}
